/**
 * Usage Tracking Hook
 * Fire-and-forget usage event tracking to the Permission Service
 */
import { useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { SilentRequest } from "@azure/msal-browser";

import { useDevBypass, devAccount } from "../utils/devBypass";
import { getConfig } from "../config";


const TOKEN_SCOPES = ["openid", "profile", "email"];

export type UsageEventType =
  | "login"
  | "tool_access"
  | "subtool_access"
  | "report_run"
  | "chat_message"
  | "report_email";

interface UsageEventPayload {
  event_type: UsageEventType;
  tool_id?: string;
  subtool_id?: string;
  details?: Record<string, string | number | boolean>;
}

/**
 * useUsageTracking - fire-and-forget usage tracking
 *
 * Usage:
 *   const { trackEvent, trackLogin, trackToolAccess } = useUsageTracking();
 *   trackLogin();
 *   trackToolAccess("cad-analysis");
 *   trackEvent({ event_type: "subtool_access", tool_id: "cad-analysis", subtool_id: "chat" });
 */
export function useUsageTracking() {
  const { instance, accounts } = useMsal();
  const account = useDevBypass ? devAccount : accounts[0];
  const msalAccount = accounts[0];
  const trackedLoginRef = useRef(false);

  /**
   * Send a usage event (fire-and-forget, never throws)
   */
  const trackEvent = useCallback(
    (payload: UsageEventPayload) => {
      try {
        const userId = useDevBypass
          ? "dev-user-id"
          : account?.localAccountId || "";
        const userEmail = useDevBypass
          ? "dev@localhost"
          : account?.username || "";
        const idTokenClaims = account?.idTokenClaims as
          | Record<string, unknown>
          | undefined;
        const roles = useDevBypass
          ? ["Portal.Admin", "Portal.User"]
          : (idTokenClaims?.roles as string[]) || [];

        if (!userId) return;

        const sendEvent = async () => {
          let authToken: string | null = null;
          if (!useDevBypass && msalAccount) {
            try {
              const request: SilentRequest = { scopes: TOKEN_SCOPES, account: msalAccount };
              const response = await instance.acquireTokenSilent(request);
              authToken = response.idToken;
            } catch {
              // Token acquisition failed - send without auth
            }
          }

          const apiBase = getConfig().permissionApiUrl || "/api";
          const url = `${apiBase}/usage/track`;
          await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(authToken ? { "Authorization": `Bearer ${authToken}` } : {}),
              "X-User-Id": userId,
              "X-User-Email": userEmail,
              "X-User-Roles": roles.join(","),
            },
            body: JSON.stringify(payload),
          });
        };

        sendEvent().catch(() => {
          // Silently ignore - usage tracking should never impact UX
        });
      } catch {
        // Silently ignore
      }
    },
    [account, instance, msalAccount]
  );

  /**
   * Track a login event (deduplicated per session)
   */
  const trackLogin = useCallback(() => {
    if (trackedLoginRef.current) return;
    trackedLoginRef.current = true;
    trackEvent({ event_type: "login" });
  }, [trackEvent]);

  /**
   * Track navigating to a tool
   */
  const trackToolAccess = useCallback(
    (toolId: string) => {
      trackEvent({ event_type: "tool_access", tool_id: toolId });
    },
    [trackEvent]
  );

  /**
   * Track navigating to a sub-tool/tab within a tool
   */
  const trackSubtoolAccess = useCallback(
    (toolId: string, subtoolId: string) => {
      trackEvent({
        event_type: "subtool_access",
        tool_id: toolId,
        subtool_id: subtoolId,
      });
    },
    [trackEvent]
  );

  return { trackEvent, trackLogin, trackToolAccess, trackSubtoolAccess };
}

export default useUsageTracking;
